import { getMe } from '@api/selectors/auth';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getSubscriptionInfo, hasActiveSubscriptionToGoal } from '@models/Me';
import { Fonts, Spacings } from '@styles/index';
import styled from '@emotion/styled';
import ChevronRightPlanner from '@icon/Planner/ChevronRightPlanner';
import Image from '@base/Image';
import NoResultFoundComponent from './NoResultFound';

const DdItemWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 560px;
  height: ${({ isGroupGoal }) => (isGroupGoal ? '34px' : '64px')};
  left: 0px;
  top: ${({ isGroupGoal }) => (isGroupGoal ? '8px' : '42px')};
  font-size: ${({ isGroupGoal }) =>
    isGroupGoal ? Spacings.SPACING_3B : Spacings.SPACING_4B};
  padding-inline: ${Spacings.SPACING_4B};
  color: ${({ isGroupGoal }) =>
    isGroupGoal ? 'var(--color-text-secondary)' : 'var(--color-text-primary)'};

  svg {
    display: ${({ isGroupGoal }) => (isGroupGoal ? 'contents' : 'block')};
    position: absolute;
    right: ${Spacings.SPACING_14};
    width: ${Spacings.SPACING_6B};
    height: ${Spacings.SPACING_6B};
  }

  .goal-icon {
    display: ${({ isGroupGoal }) => (isGroupGoal ? 'contents' : 'block')};
    height: 40px;
    width: 40px;
    margin-right: 16px;
    img {
      width: 100%;
    }
  }
`;

const DdItemInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const DdItemInfoTitle = styled.div`
  line-height: ${Spacings.SPACING_22};
  span {
    white-space: break-spaces;
  }
`;

const DdItemInfoLabel = styled.div`
  line-height: ${Spacings.SPACING_18};
  color: var(--color-text-secondary);
  font-size: ${Spacings.SPACING_3B};
  font-weight: 400;
`;

const Highlight = styled.span`
  background-color: var(--color-i-brown-6);
`;

const DropdownContainer = styled.div`
  width: 100%;
  position: absolute;
  top: 100%;
  border-radius: ${Spacings.SPACING_0B} ${Spacings.SPACING_0B}
    ${Spacings.SPACING_1B} ${Spacings.SPACING_1B};
  max-height: 60vh;
  overflow: auto;
`;

const DropdownItem = styled.div`
  white-space: normal;
  pointer-events: ${({ $isDisabled }) => ($isDisabled ? 'none' : 'auto')};
  color: ${({ $isSelected }) =>
    $isSelected ? 'var(--color-i-green)' : 'var(--color-text-primary)'};
  background-color: ${({ $isCurrent }) =>
    $isCurrent ? 'var(--color-divider)' : 'var(--color-base-1)'};
  font-weight: ${({ $isSelected, isCurrent }) =>
    $isSelected || isCurrent ? Fonts.SEMIBOLD : Fonts.REGULAR};
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'auto' : 'pointer')};
  :hover {
    background-color: ${({ $isDisabled }) =>
      $isDisabled ? 'var(--color-base-1)' : 'var(--color-divider)'};
  }
`;

const DropdownOption = ({
  option: goal,
  option,
  isActive,
  extrasProps,
  handleClickOnOption
}) => {
  const { goalSearchValue, onGoalCardClick, mappedFollowedGoals } = extrasProps;
  const start = goal.name
    .toLowerCase()
    .indexOf(goalSearchValue.trim().toLowerCase());
  const end = start + goalSearchValue.trim().length;

  const isGroupGoal = goal.children ? goal.children.length !== 0 : false;
  const me = useMyInfo(getMe);
  const subscriptionInfo = getSubscriptionInfo(me, goal.uid);
  const isFollowedGoal = !!mappedFollowedGoals[goal?.uid];

  const handleOptionClick = () => {
    if (typeof onGoalCardClick === 'function') {
      const isSubscribedGoal = hasActiveSubscriptionToGoal(me, goal.uid);
      onGoalCardClick({
        isFollowedGoal,
        isSubscribedGoal,
        goal,
        evtSource: 'Search results'
      });
    }
    if (typeof handleClickOnOption === 'function') {
      handleClickOnOption(option);
    }
  };

  if (isGroupGoal) {
    return (
      <DdItemWrapper isGroupGoal isActive={isActive} url={goal.icon}>
        {goal.name}
        <ChevronRightPlanner color="var(--color-text-secondary)" />
      </DdItemWrapper>
    );
  }

  return (
    <DdItemWrapper
      isGroupGoal={isGroupGoal}
      url={goal.icon}
      onClick={handleOptionClick}
    >
      <span className="goal-icon">
        <Image src={goal.icon} alt="" width={40} height={40} />
      </span>
      <DdItemInfo>
        <DdItemInfoTitle>
          <span>{start >= 0 && goal.name.substr(0, start)}</span>
          <Highlight>
            {start >= 0 && goal.name.substr(start, end - start)}
          </Highlight>
          <span>{start >= 0 ? goal.name.substr(end) : goal.name}</span>
        </DdItemInfoTitle>
        <DdItemInfoLabel>
          {subscriptionInfo || (isFollowedGoal && 'Free plan')}
        </DdItemInfoLabel>
      </DdItemInfo>
      <ChevronRightPlanner color="var(--color-text-secondary)" />
    </DdItemWrapper>
  );
};

const DropdownOptionList = (props) => {
  const {
    options,
    dropdownOptionProps,
    currentSelectedItem,
    selectedOption,
    handleOptionClick
  } = props;

  const handleClickOnOption = (option) => {
    if (typeof handleClickOnOption === 'function') {
      handleOptionClick(option);
    }
  };

  if (options.length === 0)
    return (
      <DropdownContainer className="dropdown">
        <NoResultFoundComponent />
      </DropdownContainer>
    );

  return (
    <DropdownContainer className="dropdown">
      {options.map((option, index) => (
        <DropdownItem
          $isSelected={option.value === selectedOption?.value}
          $isCurrent={index === currentSelectedItem}
          $isDisabled={option.disable}
          key={option.value}
        >
          <DropdownOption
            option={option}
            isActive={index === currentSelectedItem}
            extrasProps={dropdownOptionProps}
            handleClickOnOption={handleClickOnOption}
          />
        </DropdownItem>
      ))}
    </DropdownContainer>
  );
};

export default DropdownOptionList;
